import axios from 'axios';
import { isEmpty, isObjectLike, isArray, isString } from 'lodash';

import config from 'trade-in/config/index.js';
import Logger from '@sm360/logger-sentry';
import { LocaleService, ConfigsService, HTTPService, UrlService } from '@sm360/widget-commons-core-services';
import { UtilsService } from '@sm360/widget-commons-general';

/* eslint-disable no-underscore-dangle */

/**
 * Services talking to the ShowroomAPI for Canadian Black Books APIs
 * @class ShowroomCBBService
 */
class ShowroomCBBService {
    constructor() {
        /**
         * @type {String} Base path for api calls
         */
        this._basePath = `${config.endpoint.showroom}/service/v1`;

        /**
         * @type {String} Authentication parameters to add in the URI
         */
        this._authParameters = 'username=public&password=public';

        this.logger = new Logger('ShowroomCBBService', ConfigsService.getLoggerContext());
        this.CBBErrorMessages = {
            MILEAGE: 'Cbb valuation api returned an error: Mileage is outside the accepted range',
            MODEL_YEAR: 'Cbb valuation api returned an error: Model year is outside the accepted range',
            MILES_PER_YEAR: 'Cbb valuation api returned an error: Miles per year is outside the accepted range',
            NO_VALUATION: 'Vehicle has no valuation.',
        };
    }

    getTradeInToken() {
        return axios.get(`${this._basePath}/jwt?userName=sm360core&password=%23vX90*mH%3D7f26`);
    }

    _getDataForEstimation(tradeInVehicle) {
        if (!tradeInVehicle || !isObjectLike(tradeInVehicle) || isEmpty(tradeInVehicle)) {
            return null;
        }

        const km = tradeInVehicle.odometer || this._getDefaultOdometerValue(tradeInVehicle.year);

        return {
            year: String(tradeInVehicle.year),
            make: tradeInVehicle.make,
            model: tradeInVehicle.model,
            trim: tradeInVehicle.trim,
            style: tradeInVehicle.style,
            kilometers: String(km),
        };
    }

    /**
     * Private method to get an approximated odometer value for a given model year vehicle
     * @memberof ShowroomCBBService
     * @param {Number} [vehicleYear] model year of the vehicle
     * @param {?Number} [averageMileageByYear=15000] value of average odometer by year. Default: 15000. (optional)
     * @return {?Number} Integer or null
     */
    _getDefaultOdometerValue(vehicleYear, averageMileageByYear = 15000) {
        const year = UtilsService.getIntegerOrNull(vehicleYear);
        if (!year || year < 1900) {
            throw new TypeError('argument [vehicleYear] should be a safe integer higher than 1900');
        }

        const defaultMileageByYear = 15000;
        const currentYear = new Date().getFullYear();

        let odometerByYear = UtilsService.getIntegerOrNull(averageMileageByYear);
        if (!odometerByYear || odometerByYear <= 0) {
            odometerByYear = defaultMileageByYear;
        }

        let yearDiff = currentYear - year;
        if (yearDiff < -1) {
            throw new TypeError('could not obtain a negative or null value');
        }
        yearDiff = yearDiff < 0 ? -0.5 : yearDiff;

        return Math.floor((yearDiff + 1) * odometerByYear);
    }

    /**
     * Call to get vehicle information for the Canadian Black Book autocomplete
     * @memberof ShowroomCBBService
     * @param {String} [hint] The value searched
     * @param {Number} [limit=10] The desired size of the result. Default: 10
     * @returns {Promise} An array of vehicle details
     */
    searchAutocomplete(hint, limit = 10) {
        if (!hint) {
            return Promise.resolve([]);
        }

        const url =
            `${this._basePath}/vehicleEvaluation.autoComplete?${this._authParameters}` +
            `&hint=${encodeURIComponent(hint)}&limit=${limit}`;

        return axios
            .get(url)
            .then(({ data }) => {
                if (data.error) {
                    this.logger.warn(
                        'An error occurred while doing the search for the autocomplete' +
                            ` with hint [${hint}] and limit [${limit}]`,
                        data.error
                    );
                    return Promise.reject([]);
                }
                return Promise.resolve(data);
            })
            .catch((err) => {
                this.logger.error(
                    'An error occurred while doing the search for the autocomplete' +
                        ` with hint [${hint}] and limit [${limit}]`,
                    err
                );
                return Promise.reject([]);
            });
    }

    searchCatalogVehicles(query, isVIN = false, maxEntries = 10) {
        if (!query) {
            return Promise.resolve([]);
        }
        return this.getTradeInToken().then((response) => {
            const organizationId = ConfigsService.getOrganizationId();
            const organizationUnitId = ConfigsService.getOrganizationUnitId();
            const location = ConfigsService.getOrganizationUnitLocation();
            const language = LocaleService.getLocale();
            const headers = { 'x-jwt': response.data };

            const params = `orgId=${organizationId}&orgUnitId=${organizationUnitId}&location=${location}&lang=${language}&vin=${isVIN}&maxEntries=${maxEntries}`;

            const url = `${this._basePath}/tradeVehicle/catalog?${params}`;

            const catalogVehiclesList = [];
            return axios
                .post(url, query, { headers })
                .then(({ data }) => {
                    if (data.error) {
                        this.logger.warn(
                            'An error occurred while doing the search for the autocomplete' +
                                ` with hint [${query.query}] and limit [${maxEntries}]`,
                            data.error
                        );
                        return Promise.reject([]);
                    }

                    if (isArray(data.years)) {
                        data.years.map((year) => {
                            return year.makes.map((make) => {
                                return make.models.map((model) => {
                                    return model.trims.map((trim) => {
                                        return trim.styles.map((style) => {
                                            // eslint-disable-next-line prettier/prettier
                                            const vehicleString = `${year.year} ${make.make} ${model.model} ${trim.trim} ${style.style}`;
                                            const vehicleItem = {
                                                value: vehicleString,
                                                make: make.make,
                                                model: model.model,
                                                year: year.year,
                                                style: style.style,
                                                trim: trim.trim,
                                                exteriorColors: style.exteriorColours,
                                                interiorColors: style.interiorTrims,
                                                options: style.options,
                                                tradeVehicleToken: style.tradeVehicleToken,
                                                vin: style.vin,
                                            };
                                            return catalogVehiclesList.push(vehicleItem);
                                        });
                                    });
                                });
                            });
                        });
                    }
                    return Promise.resolve(catalogVehiclesList);
                })
                .catch((err) => {
                    this.logger.error(
                        'An error occurred while doing the search for the autocomplete' +
                            ` with hint [${query.query}] and limit [${maxEntries}]`,
                        err
                    );
                    return Promise.reject([]);
                });
        });
    }

    getVehicleConditionQuestions(tradeVehicleToken) {
        return this.getTradeInToken().then((response) => {
            const organizationId = ConfigsService.getOrganizationId();
            const organizationUnitId = ConfigsService.getOrganizationUnitId();
            const location = ConfigsService.getOrganizationUnitLocation();
            const language = LocaleService.getSimpleLocale();
            const headers = { 'x-jwt': response.data };

            const params = `orgId=${organizationId}&orgUnitId=${organizationUnitId}&location=${location}&lang=${language}`;

            const url = `${this._basePath}/tradeVehicle/questions?${params}`;

            return axios
                .post(url, tradeVehicleToken, { headers })
                .then(({ data }) => {
                    if (data.error) {
                        this.logger.warn(
                            'An error occurred while getting the questions for trade vehicle token : ' +
                                `[${tradeVehicleToken}] `,
                            data.error
                        );
                        return Promise.reject([]);
                    }

                    return Promise.resolve(data);
                })
                .catch((err) => {
                    this.logger.error(
                        'An error occurred while getting the questions for trade vehicle token : ' +
                            `[${tradeVehicleToken}] `,
                        err
                    );
                    return Promise.reject([]);
                });
        });
    }

    updateTradeVehicleToken(
        tradeVehicleToken,
        mileage,
        exteriorColor,
        selectedOptions,
        questions = null,
        interiorTrim = null,
        noValuation = false
    ) {
        return this.getTradeInToken().then((response) => {
            const organizationId = ConfigsService.getOrganizationId();
            const organizationUnitId = ConfigsService.getOrganizationUnitId();
            const location = ConfigsService.getOrganizationUnitLocation();
            const language = LocaleService.getSimpleLocale();
            const headers = { 'x-jwt': response.data };

            const params = `orgId=${organizationId}&orgUnitId=${organizationUnitId}&location=${location}&lang=${language}`;
            const body = { tradeVehicleToken, mileage, exteriorColor, interiorTrim, selectedOptions, questions };
            const url = `${this._basePath}/tradeVehicle/token?${params}`;

            return axios
                .post(url, body, { headers })
                .then(({ data }) => {
                    if (data.error) {
                        this.logger.warn(
                            `An error occurred while updating trade vehicle token using : [${tradeVehicleToken}] `,
                            data.error
                        );
                        return Promise.reject([]);
                    }
                    if (noValuation) return Promise.resolve(data);
                    // getVehicleValuationToken - returns valuation token
                    return this.getVehicleValuationToken(data.tradeVehicleToken).then((res) => {
                        return Promise.resolve(res);
                    });
                })
                .catch((err) => {
                    this.logger.error(
                        `An error occurred while updating trade vehicle token using : [${tradeVehicleToken}] `,
                        err
                    );
                    return Promise.reject(this.translateCBBError(err));
                });
        });
    }

    getVehicleValuationToken(tradeVehicleToken) {
        return this.getTradeInToken().then((response) => {
            const organizationId = ConfigsService.getOrganizationId();
            const organizationUnitId = ConfigsService.getOrganizationUnitId();
            const location = ConfigsService.getOrganizationUnitLocation();
            const language = LocaleService.getSimpleLocale();
            const headers = { 'x-jwt': response.data };

            const params = `orgId=${organizationId}&orgUnitId=${organizationUnitId}&location=${location}&lang=${language}`;
            const url = `${this._basePath}/tradeVehicle/valuation?${params}`;

            return axios
                .post(url, { tradeVehicleToken }, { headers })
                .then(({ data }) => {
                    if (data.error) {
                        this.logger.warn(
                            `An error occurred while fetching trade vehicle valuation using : [${tradeVehicleToken}] `,
                            data.error
                        );
                        return Promise.reject();
                    }

                    return Promise.resolve(data);
                })
                .catch((err) => {
                    this.logger.error(
                        `An error occurred while fetching trade vehicle valuation using : [${tradeVehicleToken}] `,
                        err
                    );
                    return Promise.reject(err.response.data.errMsg);
                });
        });
    }

    /**
     * Call to get trade-in vehicle id from backend api
     * @param {String} [token] Trade-in vehicle token previously got by saving trade vehicle info
     * @returns {Promise} A trade vehicle ID
     */

    getTradeInVehicleEstimation(tradeVehicleToken) {
        return this.getTradeInToken().then((response) => {
            const headers = { 'x-jwt': response.data };

            const url = `${this._basePath}/TradeInVehicle`;

            return axios
                .put(url, { tradeVehicleToken }, { headers })
                .then(({ data }) => {
                    if (data.error) {
                        this.logger.warn(
                            `An error occurred while fetching trade vehicle id using : [${tradeVehicleToken}] `,
                            data.error
                        );
                        return Promise.reject();
                    }
                    // getTradeInEstimationValue - returns trade vehicle estimation obj
                    return this.getTradeInEstimationAmount(data.id).then((res) => {
                        return Promise.resolve(res);
                    });
                })
                .catch((err) => {
                    this.logger.error(
                        `An error occurred while fetching trade vehicle id using : [${tradeVehicleToken}] `,
                        err
                    );
                    return Promise.reject(err.response.data.errMsg);
                });
        });
    }

    /**
     * Call to get trade-in vehicle estimation from Showroom backend API
     * @param {String} [Id] Trade-in vehicle Id, got by /TradeInVehicle endpoint
     * @returns {Promise} An array of vehicle estimation
     */

    getTradeInEstimationAmount(tradeVehicleId) {
        return this.getTradeInToken().then((response) => {
            const headers = { 'x-jwt': response.data };

            const url = `${this._basePath}/TradeInVehicle/${tradeVehicleId}`;

            return axios
                .get(url, { headers })
                .then(({ data }) => {
                    if (data.error) {
                        this.logger.warn(
                            `An error occurred while fetching trade vehicle estimation using : [${tradeVehicleId}] `,
                            data.error
                        );
                        return Promise.reject();
                    }

                    return Promise.resolve(data);
                })
                .catch((err) => {
                    this.logger.error(
                        `An error occurred while fetching trade vehicle estimation using : [${tradeVehicleId}] `,
                        err
                    );
                    return Promise.reject(err.response.data.errMsg);
                });
        });
    }

    /**
     * Call to get an estimated value of a vehicle by Canadian Black Book
     * @memberof ShowroomCBBService
     *
     */
    fetchEstimation(tradeInVehicle) {
        return new Promise((resolve, reject) => {
            const location = ConfigsService.getOrganizationUnitLocation();
            const obj = this._getDataForEstimation(tradeInVehicle);

            if (!obj) {
                return reject(
                    HTTPService.formatRejectResponse('invalidParams', {
                        status: 400,
                    })
                );
            }

            const url =
                `${this._basePath}/vehicleEvaluation?${this._authParameters}` +
                `&${UrlService.buildUriParametersFromCollection(obj)}&location=${location}`;

            return axios
                .get(url)
                .then(({ data }) => {
                    if (data.error) {
                        this.logger.error('An error occurred while fetching CBB estimation', data.error);
                        return reject(data.error);
                    }
                    return resolve(data);
                })
                .catch((err) => {
                    this.logger.error('An error occurred while fetching CBB estimation', err);
                    return reject(err);
                });
        });
    }

    /**
     * Static function for translating new CBB API error
     * @param {String} [message] error message as it is returned by CBB
     * @returns {String} Translated/user-friendly message
     */
    translateCBBError(message) {
        if (!isString(message)) return LocaleService.t('TRADE_IN.CBB_API_ERRORS.DEFAULT');
        switch (message) {
            case this.CBBErrorMessages.MILEAGE:
                return LocaleService.t('TRADE_IN.CBB_API_ERRORS.MILEAGE');

            case this.CBBErrorMessages.MODEL_YEAR:
                return LocaleService.t('TRADE_IN.CBB_API_ERRORS.MODEL_YEAR');

            case this.CBBErrorMessages.MILES_PER_YEAR:
                return LocaleService.t('TRADE_IN.CBB_API_ERRORS.MILES_PER_YEAR');

            case this.CBBErrorMessages.NO_VALUATION:
                return LocaleService.t('TRADE_IN.CBB_API_ERRORS.NO_VALUATION');

            default:
                return LocaleService.t('TRADE_IN.CBB_API_ERRORS.DEFAULT');
        }
    }
}

export default new ShowroomCBBService();
